/**
 * partner.mahi.fashion — Axios client.
 *
 * Own token key (`partner_mahi_token`), own base path (`/api/partner`) —
 * separate JWT audience from both consumer (aud: mira) and admin
 * (aud: admin_mahi). A partner session is never confusable with either.
 */
import axios from 'axios';

const API_BASE = process.env.REACT_APP_PARTNER_BACKEND_URL;

const partnerApi = axios.create({
  baseURL: `${API_BASE}/api/partner`,
  timeout: 20000,
});

partnerApi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('partner_mahi_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

partnerApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem('partner_mahi_token');
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export default partnerApi;

/** Coerce any backend error payload into a single user-readable string.
 *  Handles Pydantic v2 422 arrays: [{loc:[..., "field_name"], msg:"..."}]
 */
export const errMsg = (e, fallback = 'Something went wrong') => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d.length)
    return d.map((x) => {
      const field = Array.isArray(x?.loc) ? x.loc[x.loc.length - 1] : null;
      const msg = x?.msg || x?.message || 'Invalid value';
      // Make field names human-readable: "gst_number" → "GST number"
      const label = field
        ? field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
        : null;
      return label ? `${label}: ${msg}` : msg;
    }).join(' · ');
  if (d && typeof d === 'object') return d.msg || d.message || fallback;
  return e?.message || fallback;
};
